import React from 'react';
import {
  Command,
  Share2,
  ShoppingBag,
  FileText,
  Home,
  Users,
  TrendingUp,
  Settings,
  Database,
  BarChart3,
  Search,
  Download,
  Zap
} from 'lucide-react';
import { cn } from '../utils/cn';
import { CloudflareLogo } from './CloudflareLogo';

interface SidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  className?: string;
}

const sidebarItems = [
  { id: 'command', label: 'Command Center', icon: Command },
  { id: 'social', label: 'Social Media', icon: Share2 },
  { id: 'marketplace', label: 'Marketplace', icon: ShoppingBag },
  { id: 'scraper', label: 'Content Scraper', icon: FileText },
  { id: 'clients', label: 'Client Leads', icon: Users },
  { id: 'performance', label: 'Performance', icon: TrendingUp },
  { id: 'settings', label: 'Settings', icon: Settings }
];

export const Sidebar: React.FC<SidebarProps> = ({ activeSection, onSectionChange, className }) => {
  return (
    <div className={cn('w-64 bg-gray-900 text-white min-h-screen', className)}>
      {/* Header */}
      <div className="p-6 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <CloudflareLogo />
          <div>
            <img src="/AVX-logo.png" alt="AVX Logo" className="h-8 w-auto" />
            <p className="text-sm text-gray-400">System Command Dashboard</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="p-4">
        <ul className="space-y-2">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;

            return (
              <li key={item.id}>
                <button
                  onClick={() => onSectionChange(item.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left',
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* JSON Tools Section */}
      <div className="mt-8 p-4 border-t border-gray-700">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
          JSON Tools
        </h3>
        <div className="space-y-2">
          <button
            onClick={() => onSectionChange('json-analyzer')}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-left text-sm',
              activeSection === 'json-analyzer'
                ? 'bg-green-600 text-white'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            )}
          >
            <Database className="h-4 w-4" />
            <span>JSON Analyzer</span>
          </button>
          <button
            onClick={() => onSectionChange('data-visualizer')}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-left text-sm',
              activeSection === 'data-visualizer'
                ? 'bg-green-600 text-white'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            )}
          >
            <BarChart3 className="h-4 w-4" />
            <span>Data Visualizer</span>
          </button>
          <button
            onClick={() => onSectionChange('search-export')}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-left text-sm',
              activeSection === 'search-export'
                ? 'bg-green-600 text-white'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            )}
          >
            <Download className="h-4 w-4" />
            <span>Search & Export</span>
          </button>
          <button
            onClick={() => onSectionChange('quantum-analysis')}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-left text-sm',
              activeSection === 'quantum-analysis'
                ? 'bg-purple-600 text-white'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            )}
          >
            <Zap className="h-4 w-4" />
            <span>Quantum Analysis</span>
          </button>
        </div>
      </div>
    </div>
  );
};
